import React, { useEffect } from 'react';
import { Form, Input, Button, Spin } from 'antd';
import Drawer from '../../../components/AppDrawer';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  createRole,
  getPermissionCatalog,
  getRole,
  updateRole,
  type PermissionFeatureCatalogItem,
} from '../api/roles';
import RolePermissionMatrix from './RolePermissionMatrix';

function PermissionsField({
  catalog,
  value = [],
  onChange,
}: {
  catalog: PermissionFeatureCatalogItem[];
  value?: string[];
  onChange?: (value: string[]) => void;
}) {
  return (
    <RolePermissionMatrix
      catalog={catalog}
      value={value}
      onChange={(permissions) => onChange?.(permissions)}
    />
  );
}

interface RoleFormDrawerProps {
  open: boolean;
  roleId: string | null;
  onClose: () => void;
  onSaved: () => void;
}

interface FormValues {
  name: string;
  description?: string;
  permissions: string[];
}

const RoleFormDrawer: React.FC<RoleFormDrawerProps> = ({
  open,
  roleId,
  onClose,
  onSaved,
}) => {
  const [form] = Form.useForm<FormValues>();
  const isEdit = roleId !== null;

  const { data: catalog = [], isLoading: catalogLoading } = useQuery({
    queryKey: ['permission-catalog'],
    queryFn: getPermissionCatalog,
    enabled: open,
  });

  const { data: role, isLoading: roleLoading } = useQuery({
    queryKey: ['role', roleId],
    queryFn: () => getRole(roleId!),
    enabled: open && isEdit,
  });

  useEffect(() => {
    if (open && role) {
      form.setFieldsValue({
        name: role.name,
        description: role.description ?? undefined,
        permissions: role.permissions,
      });
    } else if (open && !isEdit) {
      form.resetFields();
      form.setFieldsValue({ permissions: [] });
    }
  }, [open, role, isEdit, form]);

  const saveMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload = {
        name: values.name,
        description: values.description,
        permissions: values.permissions,
      };
      if (isEdit && roleId) {
        await updateRole(roleId, payload);
      } else {
        await createRole(payload);
      }
    },
    onSuccess: onSaved,
  });

  const loading = catalogLoading || (isEdit && roleLoading);

  return (
    <Drawer
      title={isEdit ? 'Edit role' : 'Create role'}
      width={720}
      open={open}
      onClose={onClose}
      footer={
        <Button
          type="primary"
          loading={saveMutation.isPending}
          onClick={() => form.submit()}
        >
          Save
        </Button>
      }
    >
      {loading ? (
        <Spin />
      ) : (
        <Form form={form} layout="vertical" onFinish={(v) => saveMutation.mutate(v)}>
          <div className="cp-form-section">
            <Form.Item name="name" label="Name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="description" label="Description">
              <Input.TextArea rows={2} />
            </Form.Item>
          </div>
          <div className="cp-form-section">
            <Form.Item
              name="permissions"
              label="Permissions"
              rules={[{ required: true, message: 'Select at least one permission' }]}
            >
              <PermissionsField catalog={catalog} />
            </Form.Item>
          </div>
        </Form>
      )}
    </Drawer>
  );
};

export default RoleFormDrawer;
